<template>
  <div id="accordionTourTerms" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTourTerms" class="accordion-header">
        <button
          aria-controls="collapseTourTerms"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseTourTerms"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex">
            <div class="bg-secondary rounded-xs h-fit-content me-8">
              <v-icon icon="contact" size="xxl" space="p-12" />
            </div>

            <div>
              <div class="h4 ff-secondary mb-0">{{ $t("clientTour.termsConditions") }}</div>
              <div class="fw-light accordion-subtitle">{{ $t("clientTour.termsSubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTourTerms"
        aria-labelledby="headingTourTerms"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTourTerms"
      >
        <div class="accordion-body pb-32 pb-xl-48">
          <nav class="translations-tab">
            <div id="nav-tab" class="nav nav-tabs" role="tablist">
              <v-button
                v-for="(language, index) in languages"
                :id="`nav-template-day-update-basic-information-${language.key}`"
                :key="index"
                :aria-controls="`template-day-update-basic-information-${language.key}`"
                :class="{ active: language.shortName === currentLanguage }"
                :data-bs-target="`#template-day-update-basic-information-${language.key}`"
                aria-selected="true"
                class="nav-tab-link py-8 px-16 me-8"
                data-bs-toggle="tab"
                role="tab"
                size="sm"
                variant="outline-primary"
                @click="currentLanguage = language.shortName"
              >
                {{ language.label }}
              </v-button>
            </div>
          </nav>
          <form id="case-tour-update-terms-text-form" class="d-flex flex-column justify-content-between h-100">
            <fieldset v-for="language in languages" :key="language.shortName">
              <!-- <legend class="visually-hidden">{{ $t('clientTour.termsConditions') }}</legend> -->

              <div class="form-outline" v-if="currentLanguage === language.shortName">
                <label class="label">
                  <span>{{ $t("clientTour.termsConditions") }}</span>
                </label>

                <v-html-editor
                  :id="'case-tour-update-terms-text' + language.shortName"
                  ref="case-tour-update-terms-text-ref"
                  :value="termsAndConditions[language.shortName].description"
                  @input="handleInput(language.shortName, $event)"
                  @media-uploaded="editorMedia.push($event)"
                />
              </div>
            </fieldset>
            <v-button
              :is-loading="accepted"
              :disabled="disableButton"
              class="btn-icon w-fit align-self-end mt-24"
              size="sm"
              variant="primary"
              @click="updateTemplate"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="sm" space="ms-12" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, reactive, ref } from "vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import api from "@/api";
import useFormValidation from "@/helpers/form";
import Template from "@/api/objects/Template";
import useConcepts from "@/helpers/Concepts";
import { mapConcepts } from "@/helpers/ConceptHelper";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";

/* --- Types --- */
interface Translation {
  locale: string;
  description: string;
}

/* --- Props & Emits --- */
const props = defineProps<{
  agency: string;
  template: Template;
}>();
const emit = defineEmits(["reloadTemplate"]);

/* --- Toast --- */
const toast = useToast();
const { t } = useI18n();

/* --- Languages & Terms --- */
const concepts = useConcepts();
const languages = computed(() =>
  concepts.concepts.value["app.languages"]
    ? mapConcepts(concepts.concepts.value["app.languages"])
    : []
);

/* --- Terms and Conditions State --- */
const termsAndConditions = reactive({
  en: { description: "", key: "terms_and_conditions" },
  nl: { description: "", key: "terms_and_conditions" },
  es: { description: "", key: "terms_and_conditions" },
});

(props.template?.translations || []).forEach((translation: Translation) => {
  if (termsAndConditions[translation.locale]) {
    termsAndConditions[translation.locale].description = translation.description;
  }
});

/* --- Form State --- */
const form = useFormValidation("", termsAndConditions);
const { accepted, toggleAccepted } = form;
const disableButton = ref(true);
const editorMedia = ref<any[]>([]);
const currentLanguage = ref<string>(localStorage.getItem("language") || "en");

/* --- Handlers --- */
const handleInput = (language: string, value: string) => {
  termsAndConditions[language].description = value.trim();
  disableButton.value = Object.values(termsAndConditions).some((item) => item.description === "");
};

const updateTemplate = async () => {
  disableButton.value = true;
  try {
    toggleAccepted();

    const data = {
      name: props.template.name,
      travel_type: props.template.travelType,
      origin: props.template.originId,
      destination: props.template.destinationId,
      contact_text: props.template.contactText,
      complementary_text: props.template.complementaryText,
      translations: termsAndConditions,
    };

    const template = await api.activityService.updateActivity(props.agency, props.template.id, data);

    if (editorMedia.value.length > 0) {
      const editorMediaData = {
        media: editorMedia.value,
        model_type: "template",
        collection: "tinymce",
        model_id: props.template.id,
      };
      await api.media.confirm("s3", editorMediaData);
    }

    toast.success(t("general.shared.savedChanges"), {
        position: "bottom-right",
      });
    emit("reloadTemplate");
  } catch (e: any) {
    console.error(e);
    toast.error(e.response?.data?.message, { position: "bottom-right" });
  } finally {
    // disableButton.value = Object.values(termsAndConditions).some((item) => item.description === "") ? true : false;
    toggleAccepted();
  }
};
</script>
